/**
 * Client-side SafeFormulaEngine — mirrors Offers::SafeFormulaEngine (ADR-003).
 * Used for module-builder canvas preview when offer_safe_formula_engine is on.
 * Does NOT use Function()/eval. Backend remains authoritative on save/recalculate.
 *
 * Supported: + - * / ( ) , unary ±, comparisons, numbers, dotted identifiers,
 * {{mustache}}, IF / MIN / MAX / ROUND / SUM.
 */

export class SafeFormulaError extends Error {
  constructor(code, params = {}) {
    super(code);
    this.name = "SafeFormulaError";
    this.code = code;
    this.params = params;
  }
}

const TOKEN_RE =
  /\s+|(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*)|(<=|>=|==|!=|[<>])|([+\-*/(),])/y;

const MUSTACHE_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g;

const FUNCTION_NAMES = {
  IF: "IF",
  MIN: "MIN",
  MAX: "MAX",
  ROUND: "ROUND",
  SUM: "SUM",
};

export function normalizeTemplate(formula) {
  return String(formula ?? "").replace(MUSTACHE_RE, "$1");
}

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null || value === "") return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new SafeFormulaError("invalid_number", { value: String(value) });
  }
  return parsed;
};

/** ROUND_HALF_UP (away from zero), matching Ruby BigDecimal::ROUND_HALF_UP. */
export function roundHalfUp(value, digits = 0) {
  const n = toNumber(digits);
  const factor = 10 ** n;
  const scaled = toNumber(value) * factor;
  const sign = scaled < 0 ? -1 : 1;
  return (sign * Math.floor(Math.abs(scaled) + 0.5)) / factor;
}

function tokenize(input) {
  const tokens = [];
  TOKEN_RE.lastIndex = 0;
  let pos = 0;

  while (pos < input.length) {
    TOKEN_RE.lastIndex = pos;
    const match = TOKEN_RE.exec(input);
    if (!match || match.index !== pos) {
      throw new SafeFormulaError("invalid_token", {
        near: input.slice(pos, pos + 16),
      });
    }

    pos = TOKEN_RE.lastIndex;
    if (match[0].trim() === "") continue;

    if (match[1] != null) {
      tokens.push({ type: "number", value: toNumber(match[1]) });
    } else if (match[2] != null) {
      tokens.push({ type: "ident", value: match[2] });
    } else if (match[3] != null) {
      tokens.push({ type: "cmp", value: match[3] });
    } else {
      tokens.push({ type: "op", value: match[4] });
    }
  }

  return tokens;
}

function parseComparison(tokens, variables) {
  let left;
  ({ value: left, tokens } = parseExpression(tokens, variables));

  if (tokens[0]?.type === "cmp") {
    const op = tokens.shift().value;
    let right;
    ({ value: right, tokens } = parseExpression(tokens, variables));
    left = compare(left, op, right);
  }

  return { value: left, tokens };
}

function parseExpression(tokens, variables) {
  let left;
  ({ value: left, tokens } = parseTerm(tokens, variables));

  while (tokens[0]?.type === "op" && (tokens[0].value === "+" || tokens[0].value === "-")) {
    const op = tokens.shift().value;
    let right;
    ({ value: right, tokens } = parseTerm(tokens, variables));
    left = op === "+" ? left + right : left - right;
  }

  return { value: left, tokens };
}

function parseTerm(tokens, variables) {
  let left;
  ({ value: left, tokens } = parseFactor(tokens, variables));

  while (tokens[0]?.type === "op" && (tokens[0].value === "*" || tokens[0].value === "/")) {
    const op = tokens.shift().value;
    let right;
    ({ value: right, tokens } = parseFactor(tokens, variables));
    if (op === "/") {
      if (right === 0) throw new SafeFormulaError("division_by_zero");
      left /= right;
    } else {
      left *= right;
    }
  }

  return { value: left, tokens };
}

function parseFactor(tokens, variables) {
  if (!tokens.length) throw new SafeFormulaError("unexpected_end");

  const head = tokens[0];
  if (head.type === "op" && (head.value === "+" || head.value === "-")) {
    tokens.shift();
    let factor;
    ({ value: factor, tokens } = parseFactor(tokens, variables));
    return { value: head.value === "-" ? -factor : factor, tokens };
  }

  tokens.shift();

  if (head.type === "number") {
    return { value: head.value, tokens };
  }

  if (head.type === "ident") {
    if (tokens[0]?.type === "op" && tokens[0].value === "(") {
      return parseCall(head.value, tokens, variables);
    }
    if (!Object.prototype.hasOwnProperty.call(variables, head.value)) {
      throw new SafeFormulaError("unknown_variable", { name: head.value });
    }
    return { value: toNumber(variables[head.value]), tokens };
  }

  if (head.type === "op" && head.value === "(") {
    let expr;
    ({ value: expr, tokens } = parseComparison(tokens, variables));
    if (!(tokens[0]?.type === "op" && tokens[0].value === ")")) {
      throw new SafeFormulaError("missing_closing_paren");
    }
    tokens.shift();
    return { value: expr, tokens };
  }

  throw new SafeFormulaError("unexpected_token", { token: String(head.value) });
}

function parseCall(name, tokens, variables) {
  tokens.shift(); // "("
  const args = [];
  if (!(tokens[0]?.type === "op" && tokens[0].value === ")")) {
    for (;;) {
      let arg;
      ({ value: arg, tokens } = parseComparison(tokens, variables));
      args.push(arg);
      if (!(tokens[0]?.type === "op" && tokens[0].value === ",")) break;
      tokens.shift();
    }
  }
  if (!(tokens[0]?.type === "op" && tokens[0].value === ")")) {
    throw new SafeFormulaError("missing_closing_paren");
  }
  tokens.shift();
  return { value: applyFunction(name, args), tokens };
}

function applyFunction(name, args) {
  const canonical = FUNCTION_NAMES[String(name).toUpperCase()];
  if (!canonical) {
    throw new SafeFormulaError("unsupported_function", { name });
  }

  switch (canonical) {
    case "IF":
      if (args.length !== 3) throw new SafeFormulaError("wrong_arity", { name: "IF" });
      return args[0] === 0 ? args[2] : args[1];
    case "MIN":
      if (!args.length) throw new SafeFormulaError("wrong_arity", { name: "MIN" });
      return Math.min(...args);
    case "MAX":
      if (!args.length) throw new SafeFormulaError("wrong_arity", { name: "MAX" });
      return Math.max(...args);
    case "SUM":
      if (!args.length) throw new SafeFormulaError("wrong_arity", { name: "SUM" });
      return args.reduce((sum, value) => sum + value, 0);
    case "ROUND":
      if (args.length < 1 || args.length > 2) {
        throw new SafeFormulaError("wrong_arity", { name: "ROUND" });
      }
      return roundHalfUp(args[0], args[1] ?? 0);
    default:
      throw new SafeFormulaError("unsupported_function", { name });
  }
}

function compare(left, op, right) {
  switch (op) {
    case ">":
      return left > right ? 1 : 0;
    case "<":
      return left < right ? 1 : 0;
    case ">=":
      return left >= right ? 1 : 0;
    case "<=":
      return left <= right ? 1 : 0;
    case "==":
      return left === right ? 1 : 0;
    case "!=":
      return left !== right ? 1 : 0;
    default:
      throw new SafeFormulaError("unexpected_token", { token: op });
  }
}

/**
 * Identifier names used as variables (not function calls).
 * @param {string} formula
 * @returns {string[]}
 */
export function formulaDependencies(formula) {
  try {
    const tokens = tokenize(normalizeTemplate(formula));
    const names = [];
    tokens.forEach((token, index) => {
      if (token.type !== "ident") return;
      const next = tokens[index + 1];
      if (next?.type === "op" && next.value === "(") return;
      if (FUNCTION_NAMES[String(token.value).toUpperCase()]) return;
      names.push(token.value);
    });
    return [...new Set(names)];
  } catch (error) {
    return [];
  }
}

/**
 * @param {string} formula
 * @param {Record<string, number|string>} variables
 * @returns {number}
 */
export function evaluateSafeFormula(formula, variables = {}) {
  const normalized = normalizeTemplate(formula);
  const tokens = tokenize(normalized);
  if (!tokens.length) throw new SafeFormulaError("empty_formula");

  const stringKeys = Object.fromEntries(
    Object.entries(variables || {}).map(([key, value]) => [String(key), value])
  );

  const { value, tokens: rest } = parseComparison(tokens, stringKeys);
  if (rest.length) {
    throw new SafeFormulaError("unexpected_token", {
      token: String(rest[0]?.value),
    });
  }

  return value;
}

/** Syntax check with dummy 1 for unknown variables (mirrors Ruby validate). */
export function validateSafeFormula(formula) {
  const dependencies = formulaDependencies(formula);
  const dummy = Object.fromEntries(dependencies.map((name) => [name, 1]));
  try {
    evaluateSafeFormula(formula, dummy);
    return { ok: true, error: null, dependencies };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof SafeFormulaError ? error.code : "invalid",
      dependencies,
    };
  }
}

/** Map engine error codes → i18n keys under offer.calculation.module.formula.error.* */
export function safeFormulaErrorI18nKey(code) {
  const known = {
    empty_formula: "empty",
    division_by_zero: "division_by_zero",
    unknown_variable: "unknown_variable",
    invalid_token: "invalid",
    invalid_number: "invalid",
    unexpected_end: "invalid",
    unexpected_token: "invalid",
    missing_closing_paren: "invalid",
    unsupported_function: "unsupported_function",
    wrong_arity: "wrong_arity",
  };
  return `offer.calculation.module.formula.error.${known[code] || "invalid"}`;
}
